import { Node } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { Document } from '@tiptap/extension-document';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Paragraph } from '@tiptap/extension-paragraph';
import {
  Table,
  TableHeader,
  TableRow,
  TableCell
} from '@tiptap/extension-table';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { UndoRedo } from '@tiptap/extensions';
import { cellAround, nextCell } from '@tiptap/pm/tables';
import { useEditor, EditorContent } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useReduxState } from 'hooks';
import {
  CodeExtensionWithKeyboardShortcuts,
  RichTextEditorMenu
} from 'components/common/richTextEditor';

export type TableEditorHandler = {
  handleInsertColumn: () => void;
  handleInsertRow: () => void;
  handleRemoveColumn: () => void;
  handleRemoveRow: () => void;
};

interface Props {
  content: string;
  isPreview: boolean;
  layout?: 'auto' | 'fixed';
  onChange: (content: string) => void;
  preventStructuralChanges: boolean;
  ref: React.RefObject<TableEditorHandler>;
}

const TableEditor = ({
  content,
  isPreview,
  layout = 'auto',
  onChange,
  preventStructuralChanges,
  ref
}: Props) => {
  const { theme } = useReduxState();

  const lastEmittedRef = useRef<string | null>(null);
  const tableElementRef = useRef<HTMLDivElement>(undefined);
  const updateValueTimeout = useRef<NodeJS.Timeout>(undefined);

  const editor = useEditor({
    editorProps: { attributes: { class: 'vuo-table-editor' } },
    extensions: [
      Node.create({
        name: 'backspace-highjack'
      }).extend({
        addKeyboardShortcuts() {
          return {
            Backspace: ({ editor }) => {
              // get contents of the current cell
              const currentCell = editor.state.selection.$from.parent;

              // if cell is empty, do not allow backspace
              // for some reason allowing this causes two new
              // columns to be created to the right of the table
              const currentCellContent = currentCell.textContent;
              if (!currentCellContent.trim().length) return true;
              return false;
            }
          };
        }
      }),
      Bold,
      CodeExtensionWithKeyboardShortcuts,
      Document,
      Italic,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: null, target: null }
      }),
      Paragraph,
      Text,
      Table.configure({ lastColumnResizable: false, resizable: false }).extend({
        addKeyboardShortcuts: () => {
          return {
            Enter: ({ editor }) => {
              const result = updateValue(editor, 'addRowAfter');
              if (!result) return true;

              const $currentCell = cellAround(editor.state.selection.$from);
              if (!$currentCell) return false;

              const $nextCell = nextCell($currentCell, 'vert', 1);
              if (!$nextCell) return false;

              editor.commands.focus($nextCell.pos);
              return true;
            },
            'Shift-Tab': ({ editor }) => {
              const $currentCell = cellAround(editor.state.selection.$from);
              if (!$currentCell) return false;

              editor.commands.goToPreviousCell();
              return true;
            },
            Tab: ({ editor }) => {
              const $currentCell = cellAround(editor.state.selection.$from);
              if (!$currentCell) return false;

              const hasNextRight = nextCell($currentCell, 'horiz', 1);
              const hasNextDown = nextCell($currentCell, 'vert', 1);
              if (!hasNextRight && !hasNextDown) {
                const result = updateValue(editor, 'addRowAfter');
                if (!result) return true;
              }

              editor.commands.goToNextCell();
              return true;
            }
          };
        }
      }),
      TableHeader,
      TableRow,
      TableCell.extend({ content: 'inline*' }),
      Underline,
      UndoRedo
    ],
    content,
    immediatelyRender: false,
    onUpdate({ editor }) {
      const editorValue = editor.getHTML();
      if (content === editorValue) return;

      clearTimeout(updateValueTimeout.current);
      updateValueTimeout.current = setTimeout(() => {
        lastEmittedRef.current = editorValue;
        onChange(editorValue);
      }, 500);
    }
  });

  useEffect(() => {
    return () => {
      clearTimeout(updateValueTimeout.current);
    };
  }, []);

  const updateValue = useCallback(
    (tableEditor: typeof editor, command: string) => {
      if (preventStructuralChanges || isPreview) return;

      tableEditor.chain().focus()[command]().run();
      return true;
    },
    [preventStructuralChanges, isPreview]
  );

  const handler = useMemo(
    () => ({
      handleInsertColumn: () => updateValue(editor, 'addColumnAfter'),
      handleInsertRow: () => updateValue(editor, 'addRowAfter'),
      handleRemoveColumn: () => updateValue(editor, 'deleteColumn'),
      handleRemoveRow: () => updateValue(editor, 'deleteRow')
    }),
    [editor, updateValue]
  );

  useEffect(() => {
    if (!editor) return;

    // if this is triggered by the update we just sent, avoid
    if (content === lastEmittedRef.current) {
      lastEmittedRef.current = null;
      return;
    }

    if (content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
      lastEmittedRef.current = null;
    }
  }, [content, editor]);

  useEffect(() => {
    if (!ref) return;
    ref.current = handler;
  }, [handler, ref]);

  return (
    <div className="table-editor" ref={tableElementRef}>
      <EditorContent editor={editor} />

      {editor ? (
        <RichTextEditorMenu
          editor={editor}
          richTextElementRef={tableElementRef}
        />
      ) : null}

      <style jsx global>{`
        .vuo-table-editor {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColor};
          font-size: 1.6rem;
          min-width: 100%;
          outline: none;
          transition: border-color 0.2s;
        }
        .vuo-table-editor table {
          border-collapse: collapse;
          border-spacing: 0;
          overflow-x: auto;
          table-layout: ${layout};
          width: 100%;
        }
        .vuo-table-editor th,
        .vuo-table-editor td {
          border-left: 1px solid ${theme.lightGreyDark};
          border-top: 1px solid ${theme.lightGreyDark};
          min-width: 50px;
          padding: 7px 8px;
          text-align: left;
          transition: border-color 0.2s;
        }
        .vuo-table-editor th:first-child,
        .vuo-table-editor td:first-child {
          border-left: none;
        }
        .vuo-table-editor tr:first-child th,
        .vuo-table-editor tr:first-child td {
          border-top: none;
        }
        .vuo-table-editor:focus,
        .vuo-table-editor:focus th,
        .vuo-table-editor:focus td {
          border-color: #a1a8aa;
        }
        .vuo-table-editor a {
          color: ${theme.linkColor};
        }
        .vuo-table-editor code {
          background-color: ${theme.lightGreyLighter};
          border-radius: 4px;
          font-size: 1.4rem;
          padding: 2px 4px;
        }
      `}</style>
    </div>
  );
};

export default TableEditor;
